import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatchedActions, useTranslation } from "../../../../../hooks";
import { IconSvg } from "../../../../../utils/constants/icons/iconSvg.constant";
import { Solutions } from "../Solutions/Solutions";
import { classNames } from "../../../../../utils/helpers/classNames";
import { PROXY_TYPES } from "../../../../../utils/constants";
import { Actions } from "../Actions/Actions";
import { SearchCountries } from "../SearchCountries/SearchCountries";
import { capitalize } from "../../../../../utils/helpers";
import "./MobileMenu.scss";

export const MobileMenu = () => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const proxyListRef = useRef(null);
  const proxyListWrapperRef = useRef(null);

  const {
    proxy,
    main: { buyBtn },
    header: {
      menu: { price, feedback, faq, contacts, back },
      solutions: { solutionsNav }
    }
  } = useTranslation();

  const { proxies } = useSelector((state) => state.content);
  const { isMenuOpened } = useSelector((state) => state.header);
  const { setMenu, setActiveProxy, setActiveCountry } = useDispatchedActions();

  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isProxyListOpen, setIsProxyListOpen] = useState(false);
  const [selectedProxyType, setSelectedProxyType] = useState(null);

  const resetMenu = () => {
    const proxyListWrapper = proxyListWrapperRef?.current;

    if (proxyListWrapper) {
      proxyListWrapper.style.height = "0px";
    }

    setIsSolutionsOpen(false);
    setIsProxyListOpen(false);
    setSelectedProxyType(null);
  };

  useEffect(() => {
    if (isMenuOpened) {
      document.body.classList.add("disable-scroll");
      return;
    }

    document.body.classList.remove("disable-scroll");
    resetMenu();
  }, [isMenuOpened]);

  const closeMobileMenu = () => setMenu(false);

  const solutionsHandler = ({ target }) => {
    if (!target.closest(".mobile-menu__expand-item")) return;

    setIsProxyListOpen(false);
    if (proxyListWrapperRef?.current) proxyListWrapperRef.current.style.height = "0px";

    setIsSolutionsOpen((current) => !current);
  };

  const proxyListHandler = ({ target }) => {
    if (!target.closest(".mobile-menu__expand-item")) return;

    setIsSolutionsOpen(false);

    const proxyListWrapper = proxyListWrapperRef?.current;
    const proxyListHeight = proxyListRef?.current?.offsetHeight;

    if (!proxyListWrapper || !proxyListHeight) return;

    if (!isProxyListOpen) {
      proxyListWrapper.style.height = `${proxyListHeight}px`;
      setIsProxyListOpen(true);

      return;
    }
    proxyListWrapper.style.height = "0px";
    setIsProxyListOpen(false);
  };

  const proxyTypeHandler = (proxy) => {
    if (proxy?.code === PROXY_TYPES.mobile.code) {
      navigate(`/${lang}/mobile`);
      closeMobileMenu();
      return;
    }

    setSelectedProxyType(proxy);
  };

  const getProxyLabel = ({ code: proxyCode, label }) => {
    if (proxyCode === PROXY_TYPES.isp.code || proxyCode === PROXY_TYPES.mobile.code) {
      return `${label} ${proxy}`;
    }

    return `${capitalize(proxy)} ${label}`;
  };

  const countryHandler = (e, proxy, country) => {
    setActiveProxy(proxy);
    setActiveCountry({
      id: country?.id || "",
      code: country?.code || ""
    });

    closeMobileMenu();
  };

  return (
    <div className="mobile-menu">
      <div className={classNames(
        "mobile-menu__container",
        { "mobile-menu__container--proxy-menu": !!selectedProxyType }
      )}>
        <ul className="mobile-menu__list">
          <li
            onClick={proxyListHandler}
            className={classNames(
              "mobile-menu__item mobile-menu__item--buy-proxy",
              { "mobile-menu__item--open": isProxyListOpen }
            )}>
            <div className="mobile-menu__expand-item">
              <div>{buyBtn}</div>
              <IconSvg className="mobile-menu__item-icon" tag="arrowRight"/>
            </div>
            <div className="mobile-menu__proxy-list-wrapper" ref={proxyListWrapperRef}>
              <ul className="mobile-menu__proxy-list" ref={proxyListRef}>
                {proxies?.enabled?.map(({ code, label, value }) => (
                  <li
                    key={label}
                    className="mobile-menu__proxy-item"
                    onClick={() => proxyTypeHandler({ code, label, value })}
                  >
                    <div>{getProxyLabel({ code, label })}</div>
                    {code !== PROXY_TYPES.mobile.code && <IconSvg tag="arrowRight"/>}
                  </li>
                ))}
              </ul>
            </div>
          </li>
          <li
            onClick={solutionsHandler}
            className={classNames(
              "mobile-menu__item mobile-menu__item--solutions",
              { "mobile-menu__item--open": isSolutionsOpen }
            )}>
            <div className="mobile-menu__expand-item">
              <div>{solutionsNav}</div>
              <IconSvg className="mobile-menu__item-icon" tag="arrowRight"/>
            </div>
            <div className="mobile-menu__solutions-list">
              <Solutions onClose={closeMobileMenu}/>
            </div>
          </li>
          <li className="mobile-menu__item">
            <Link to={`/${lang}/#prices`}>{price}</Link>
          </li>
          <li className="mobile-menu__item">
            <Link to={`/${lang}/feedback`} onClick={closeMobileMenu}>{feedback}</Link>
          </li>
          <li className="mobile-menu__item">
            <Link to={`/${lang}/#faq`}>{faq}</Link>
          </li>
          <li className="mobile-menu__item">
            <Link to={`/${lang}/contacts/`} onClick={closeMobileMenu}>{contacts}</Link>
          </li>
        </ul>
        <div className={classNames(
          "mobile-menu__proxy-menu",
          { "mobile-menu__proxy-menu--open": !!selectedProxyType }
        )}>
          <div className="header__countries">
            <button className="header__go-back" onClick={() => setSelectedProxyType(null)}>
              <IconSvg tag="arrowRight" className="header__go-back-arrow"/>
              <div>{back}</div>
            </button>
            <SearchCountries
              proxy={selectedProxyType || {}}
              handlerClick={countryHandler}
            />
          </div>
        </div>
      </div>
      <Actions isMobileMenu={true}/>
    </div>
  );
};
